import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

export default function ReferralConsentPage() {
  const { token } = useParams();
  const [state, setState] = useState('loading'); // loading | ready | submitting | accepted | declined | error | expired | already_responded
  const [details, setDetails] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`/api/partners/consent/${token}`);
        const d = await r.json();
        if (r.status === 410) {
          setState(d.status === 'expired' ? 'expired' : 'already_responded');
          setErrorMsg(d.error || '');
          return;
        }
        if (!r.ok) {
          setState('error');
          setErrorMsg(d.error || 'This consent request could not be found.');
          return;
        }
        setDetails(d);
        setState('ready');
      } catch {
        setState('error');
        setErrorMsg('Network error — please try again.');
      }
    })();
  }, [token]);

  const respond = async (decision) => {
    setState('submitting');
    try {
      const r = await fetch(`/api/partners/consent/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision }),
      });
      const d = await r.json();
      if (!r.ok) {
        setState(d.status === 'expired' ? 'expired' : 'error');
        setErrorMsg(d.error || 'Something went wrong.');
        return;
      }
      setState(decision === 'accepted' ? 'accepted' : 'declined');
    } catch {
      setState('error');
      setErrorMsg('Network error — please try again.');
    }
  };

  const expiryDate = details?.expires_at
    ? new Date(details.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  return (
    <div style={{ minHeight: '100vh', background: '#f0f9ff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ maxWidth: '520px', width: '100%' }}>

        {/* Brand header */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', background: '#0e1b2c', borderRadius: '12px', padding: '10px 20px' }}>
            <span style={{ color: '#10b981', fontSize: '18px', fontWeight: 800, letterSpacing: '-0.02em' }}>Broker</span>
            <span style={{ color: '#fff', fontSize: '18px', fontWeight: 800, letterSpacing: '-0.02em' }}>Connect</span>
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', padding: '36px', overflow: 'hidden' }}>

          {/* LOADING */}
          {state === 'loading' && (
            <div style={{ textAlign: 'center', color: '#6b7280', padding: '40px 0' }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
              <div>Loading your consent request…</div>
            </div>
          )}

          {/* READY — show details and accept/decline */}
          {state === 'ready' && details && (
            <>
              <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                <div style={{ fontSize: '28px', marginBottom: '10px' }}>🔐</div>
                <h1 style={{ fontSize: '20px', fontWeight: 800, color: '#0e1b2c', margin: '0 0 8px 0' }}>
                  Data sharing consent request
                </h1>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
                  Hi {details.client_name}, your broker would like your permission to share your contact details with a specialist service provider.
                </p>
              </div>

              {/* Details card */}
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  {[
                    ['Your broker', details.broker_name || details.broker_firm],
                    ['Broker firm', details.broker_firm],
                    ['Referred to', details.partner_name],
                    ['Service', details.category],
                  ].map(([label, value]) => value ? (
                    <div key={label}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: '3px' }}>{label}</div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#0e1b2c' }}>{value}</div>
                    </div>
                  ) : null)}
                </div>
              </div>

              {/* What will be shared */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#374151', marginBottom: '8px' }}>What will be shared if you accept:</div>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {['Your full name', 'Your email address', 'Your phone number (if provided)', 'Any notes your broker included'].map(item => (
                    <li key={item} style={{ fontSize: '13px', color: '#374151', marginBottom: '4px' }}>{item}</li>
                  ))}
                </ul>
              </div>

              {/* GDPR note */}
              <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px', padding: '12px', marginBottom: '24px', fontSize: '12px', color: '#92400e', lineHeight: 1.5 }}>
                Your data will only be used to contact you about {details.purpose}. You can withdraw consent at any time. This request expires on {expiryDate}.
              </div>

              {/* Buttons */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <button
                  onClick={() => respond('declined')}
                  style={{ padding: '14px', border: '2px solid #e5e7eb', borderRadius: '10px', background: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 700, color: '#6b7280' }}
                >
                  ✕ Decline
                </button>
                <button
                  onClick={() => respond('accepted')}
                  style={{ padding: '14px', border: 'none', borderRadius: '10px', background: '#10b981', cursor: 'pointer', fontSize: '14px', fontWeight: 700, color: '#fff' }}
                >
                  ✓ Accept &amp; share details
                </button>
              </div>
            </>
          )}

          {/* SUBMITTING */}
          {state === 'submitting' && (
            <div style={{ textAlign: 'center', color: '#6b7280', padding: '40px 0' }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
              <div>Recording your response…</div>
            </div>
          )}

          {/* ACCEPTED */}
          {state === 'accepted' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
              <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#065f46', marginBottom: '12px' }}>Thank you!</h2>
              <p style={{ fontSize: '14px', color: '#374151', lineHeight: 1.6 }}>
                Your details have been shared with <strong>{details?.partner_name}</strong>. They will be in touch with you shortly.
              </p>
              <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '20px' }}>
                You can close this page.
              </p>
            </div>
          )}

          {/* DECLINED */}
          {state === 'declined' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>👍</div>
              <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0e1b2c', marginBottom: '12px' }}>No problem at all</h2>
              <p style={{ fontSize: '14px', color: '#374151', lineHeight: 1.6 }}>
                Your details will <strong>not</strong> be shared. This referral has been cancelled.
              </p>
              <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '20px' }}>
                You can close this page.
              </p>
            </div>
          )}

          {/* EXPIRED */}
          {(state === 'expired') && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏰</div>
              <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#92400e', marginBottom: '12px' }}>This request has expired</h2>
              <p style={{ fontSize: '14px', color: '#374151', lineHeight: 1.6 }}>
                The consent window has passed. If you'd still like to proceed, please ask your broker to send a new request.
              </p>
            </div>
          )}

          {/* ALREADY RESPONDED */}
          {state === 'already_responded' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>ℹ️</div>
              <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0e1b2c', marginBottom: '12px' }}>Already responded</h2>
              <p style={{ fontSize: '14px', color: '#374151', lineHeight: 1.6 }}>
                You've already responded to this consent request. No further action is needed.
              </p>
            </div>
          )}

          {/* ERROR */}
          {state === 'error' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
              <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#991b1b', marginBottom: '12px' }}>Something went wrong</h2>
              <p style={{ fontSize: '14px', color: '#374151', lineHeight: 1.6 }}>{errorMsg || 'Please check the link in your email and try again.'}</p>
            </div>
          )}
        </div>

        <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '12px', color: '#9ca3af' }}>
          Powered by BrokerConnect &nbsp;·&nbsp; Your data is handled in accordance with GDPR
        </div>
      </div>
    </div>
  );
}
